import { useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { MpvLookupResult, MsxService } from 'src/services/MsxService';

interface UseQueryMpvLookupOptions {
    enabled?: boolean;
    debounceMs?: number;
}

export type MpvLookupQuery = UseQueryResult<MpvLookupResult | null, Error> & {
    resolvedFor: { mpvId: string; cultures: string[] };
};

export const useQueryMpvLookup = (
    mpvId: string,
    cultures: string[],
    { enabled = true, debounceMs = 400 }: UseQueryMpvLookupOptions = {},
): MpvLookupQuery => {
    const [debouncedMpvId, setDebouncedMpvId] = useState(mpvId);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedMpvId(mpvId), debounceMs);

        return (): void => clearTimeout(handle);
    }, [mpvId, debounceMs]);

    const culturesKey = cultures.join('|');

    const query = useQuery<MpvLookupResult | null, Error>(
        ['mpvLookup', debouncedMpvId, culturesKey],
        ({ signal }) => MsxService.getProductKeyByMpvId(debouncedMpvId, cultures, signal),
        {
            enabled: enabled && !!debouncedMpvId && cultures.length > 0,
            refetchOnWindowFocus: false,
            retry: false,
            suspense: false,
        },
    );

    return Object.assign(query, { resolvedFor: { mpvId: debouncedMpvId, cultures } });
};
